import type { BillingDataPeriod } from 'reactApp/api/billing/data/BillingDataAPICall.types';
import type { BillingDataGrace, BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import {
    getCurrentProductPeriod,
    getCurrentProductPrice,
    getCurrentSubscriptionPeriod,
} from 'reactApp/modules/billingData/billingData.utils';
import type { Product } from 'reactApp/types/Billing';
import { approxMonthPeriodInDays, getDays, getPeriodName, isYear1Period, PERIODS } from 'reactApp/utils/Period';
import { formatPrice } from 'reactApp/utils/priceHelpers';

export interface GracePaidInfo {
    paidPriceText?: string;
    totalPriceText: string;
}

function getGraceExplanationPeriodPostfixText(period?: string): { partial: string; longLiving: string } {
    switch (period) {
        case PERIODS.month:
            return { partial: 'за\u00A0тариф на\u00A0месяц', longLiving: 'из\u00A0тарифа на\u00A0месяц' };
        case PERIODS.threeMonths:
            return { partial: 'за\u00A0тариф на\u00A03\u00A0месяца', longLiving: 'из\u00A0тарифа на\u00A03\u00A0месяца' };
        case PERIODS.sixMonths:
            return { partial: 'за\u00A0тариф на\u00A06\u00A0месяцев', longLiving: 'из\u00A0тарифа на\u00A06\u00A0месяцев' };
        case PERIODS.year:
            return { partial: 'за\u00A0тариф на\u00A0год', longLiving: 'из\u00A0тарифа на\u00A0год' };
        case PERIODS.twoYears:
            return { partial: 'за\u00A0тариф на\u00A02\u00A0года', longLiving: 'из\u00A0тарифа на 2\u00A0года' };
        default:
            return { partial: 'за\u00A0тариф', longLiving: 'из\u00A0тарифа' };
    }
}

function getTextGracePeriod({ currentProductPeriod, paid, fullPrice }: { currentProductPeriod: string; paid: number; fullPrice: number }) {
    const currentPeriodInDays = getDays(currentProductPeriod);
    const reservedPeriod = (currentPeriodInDays * paid) / fullPrice;
    const approxPeriod = approxMonthPeriodInDays(reservedPeriod);
    return getPeriodName(approxPeriod, false, true);
}

export function getGraceSubscriptionText({
    grace,
    product,
    currentPeriod,
    isAutoRenewable,
}: {
    currentPeriod?: BillingDataPeriod;
    grace?: BillingDataGrace;
    product: Product;
    isAutoRenewable?: boolean;
}) {
    if (!grace) {
        return;
    }

    const currentProductPeriod = getCurrentProductPeriod(product, currentPeriod);
    const currentPeriodText = isYear1Period(currentProductPeriod) ? 'год' : 'период';
    const currentPeriodPrice = getCurrentProductPrice(product, currentPeriod);

    const getFallbackPostfixText = (reminder = currentPeriodPrice) =>
        `Убедитесь, что на\u00A0карте есть ${formatPrice(reminder, 'ru')}\u00A0₽, чтобы продлить подписку`;

    const getFallbackText = (reminder = currentPeriodPrice) => `Не\u00A0удалось списать оплату. ${getFallbackPostfixText(reminder)}`;

    if (!isAutoRenewable) {
        return `Вы\u00A0отключили автоплатёж\u00A0— подписка может не\u00A0продлиться. Включите его, чтобы сохранить все возможности Mail Space`;
    }

    if (grace.partial && !grace.partialLongLiving) {
        const { cummulativeTotal, remainder } = grace.partial;
        if (!cummulativeTotal) {
            return getFallbackText(remainder);
        }

        return `Оплата списалась частично\u00A0— ${formatPrice(cummulativeTotal, 'ru')}\u00A0₽. Доплатите ${formatPrice(
            remainder,
            'ru'
        )}\u00A0₽ и\u00A0сохраните подписку`;
    }

    if (grace.partial && grace.partialLongLiving) {
        const { cummulativeTotal, remainder } = grace.partial;
        const { cummulativeTotal: cummulativeTotalLL, remainder: remainderLL } = grace.partialLongLiving;
        if (!cummulativeTotal) {
            return getFallbackText(remainder);
        }
        const textPeriod = getTextGracePeriod({
            currentProductPeriod,
            fullPrice: cummulativeTotalLL + remainderLL,
            paid: cummulativeTotal || cummulativeTotalLL,
        });

        return `Оплата списалась частично\u00A0— хватит примерно на\u00A0${textPeriod}. Доплатите ${formatPrice(
            remainder,
            'ru'
        )}\u00A0₽ и\u00A0сохраните подписку на\u00A0весь ${currentPeriodText}`;
    }

    if (!grace.partial && grace.partialLongLiving) {
        const { remainder, lastInterval } = grace.partialLongLiving;

        const textPeriod = getPeriodName(lastInterval, false, true);

        return `Оплата списалась частично\u00A0— только за\u00A0${textPeriod}. Доплатите ${formatPrice(
            remainder,
            'ru'
        )}\u00A0₽ и\u00A0сохраните подписку на\u00A0весь ${currentPeriodText}`;
    }

    return getFallbackText();
}

export function getGraceSubscriptionShortText(subscription: BillingDataSubscriptionInfo) {
    const { grace, productPeriod } = subscription;

    if (!grace) {
        return '';
    }
    const { longLiving: longLivingPostfix, partial: partialPostfix } = getGraceExplanationPeriodPostfixText(productPeriod);
    if (grace.partial && !grace.partialLongLiving) {
        return `Списана только часть платежа ${partialPostfix}`;
    }

    if (grace.partial && grace.partialLongLiving) {
        const { cummulativeTotal } = grace.partial;
        const { cummulativeTotal: cummulativeTotalLL, remainder: remainderLL } = grace.partialLongLiving;
        const currentSubsPeriod = getCurrentSubscriptionPeriod(subscription);
        const textPeriod = getTextGracePeriod({
            currentProductPeriod: currentSubsPeriod,
            fullPrice: cummulativeTotalLL + remainderLL,
            paid: cummulativeTotal || cummulativeTotalLL,
        });
        return `Списана часть платежа\u00A0— хватит примерно на\u00A0${textPeriod} ${longLivingPostfix}`;
    }

    if (!grace.partial && grace.partialLongLiving) {
        const { lastInterval } = grace.partialLongLiving;
        const textPeriod = getPeriodName(lastInterval, false, true);
        return `Списана только часть платежа\u00A0— за\u00A0${textPeriod} ${longLivingPostfix}`;
    }

    return '';
}

export function getGracePaidInfo(grace?: BillingDataGrace): GracePaidInfo | null {
    if (!grace) {
        return null;
    }

    if (grace.partial && !grace.partialLongLiving) {
        const { cummulativeTotal, remainder } = grace.partial;
        return {
            paidPriceText: cummulativeTotal ? `${formatPrice(cummulativeTotal, 'ru')}\u00A0₽` : undefined,
            totalPriceText: `${formatPrice(cummulativeTotal + remainder, 'ru')}\u00A0₽`,
        };
    }
    if (grace.partial && grace.partialLongLiving) {
        const { cummulativeTotal } = grace.partial;
        const { cummulativeTotal: cummulativeTotalLL, remainder: remainderLL } = grace.partialLongLiving;
        const paid = cummulativeTotal + cummulativeTotalLL;
        return {
            paidPriceText: paid ? `${formatPrice(cummulativeTotal + cummulativeTotalLL, 'ru')}\u00A0₽` : undefined,
            totalPriceText: `${formatPrice(cummulativeTotalLL + remainderLL, 'ru')}\u00A0₽`,
        };
    }

    if (!grace.partial && grace.partialLongLiving) {
        const { cummulativeTotal, remainder } = grace.partialLongLiving;
        return {
            paidPriceText: cummulativeTotal ? `${formatPrice(cummulativeTotal, 'ru')}\u00A0₽` : undefined,
            totalPriceText: `${formatPrice(cummulativeTotal + remainder, 'ru')}\u00A0₽`,
        };
    }
    return null;
}
